import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Path, UseFormReturn } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ClaimAnnotationsPanel,
  ClaimEdit,
  ClaimLeftSidebarContent,
  ClaimLineageBanner,
  ClaimRebuildButton,
  ClaimScrubButton,
  ClaimSubmitButton
} from '@features/billing/components/Claim';
import type { CMS1500FormData } from '@features/billing/components/Claim/ClaimCMS1500Form';
import { AnnotationsPanelErrorBoundary } from '@features/billing/components/Claim/ClaimScrubber/AnnotationsPanelErrorBoundary';
import { mapToFormField } from '@features/billing/components/Claim/ClaimScrubber/utils/fieldMapping';
import { DeleteClaimButton } from '@features/billing/components/Claim/DeleteClaimButton';
import { ResizableSidebar } from '@features/billing/components/Claim/ResizableSidebar';
import { useGetClaimByIdQuery } from '@features/billing/store/api';
import { useClaimScrubberWithUI } from '@features/billing/store/hooks';
import { selectAnnotationCounts, selectShouldShowAnnotations } from '@features/billing/store/selectors';
import type { Claim, ClaimAnnotation } from '@features/billing/types';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { Box, Container, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

import { type AutosaveState, AutosaveStatus } from '../components/AutosaveStatus';
import { useStickyHeaderDetection } from '../hooks/useStickyHeaderDetection';

/**
 * Custom hook to sync claim annotations with form errors
 * This ensures errors are only processed when claim is scrubbed and has annotations
 */
const useClaimScrubErrorSync = (
  claim: Claim | null | undefined,
  formMethods: UseFormReturn<CMS1500FormData> | null
) => {
  const lastAnnotationSignatureRef = useRef<string | null>(null);
  
  // Memoize annotation signature to avoid expensive recalculation on every render
  const annotationSignature = useMemo(() => {
    if (!claim?.annotations || claim.annotations.length === 0) return 'no-annotations';
    
    // Create a lightweight signature without full JSON.stringify for better performance
    // Only track the fields that matter for error display
    return claim.annotations
      .map((a: ClaimAnnotation) => `${a.field}:${a.severity}:${a.message}`)
      .sort()
      .join('|');
  }, [claim?.annotations]);

  useEffect(() => {
    if (!formMethods) return;
    
    // Only process if annotations have actually changed
    if (annotationSignature !== lastAnnotationSignatureRef.current) {
      lastAnnotationSignatureRef.current = annotationSignature;
      
      // Get current form errors
      const currentErrors = formMethods.formState.errors;
      const errorKeys = Object.keys(currentErrors);
      
      // Track which fields currently have scrubber errors
      const currentScrubberFields = new Set<string>();
      errorKeys.forEach(key => {
        const error = currentErrors[key as keyof typeof currentErrors];
        if (error && typeof error.type === 'string' && error.type.startsWith('scrubber-')) {
          currentScrubberFields.add(key);
        }
      });
      
      // Track which fields should have errors based on new annotations
      const newScrubberFields = new Set<string>();
      
      if (claim?.annotations && claim.annotations.length > 0) {
        // Build map of fields that should have errors
        claim.annotations.forEach((annotation: ClaimAnnotation) => {
          if (annotation.field) {
            const formPath = mapToFormField(annotation.field, claim.claim_lines);
            if (formPath) {
              newScrubberFields.add(formPath);
            }
          }
        });
        
        // Clear errors for fields that no longer have annotations
        currentScrubberFields.forEach(field => {
          if (!newScrubberFields.has(field)) {
            formMethods.clearErrors(field as Path<CMS1500FormData>);
          }
        });
        
        // Set errors for fields with annotations
        claim.annotations.forEach((annotation: ClaimAnnotation) => {
          if (annotation.field) {
            const formPath = mapToFormField(annotation.field, claim.claim_lines);
            if (formPath) {
              // Only set error if it's different from current error
              const currentError = currentErrors[formPath as keyof typeof currentErrors];
              const newType = `scrubber-${annotation.severity}`;
              if (!currentError || currentError.type !== newType || currentError.message !== annotation.message) {
                formMethods.setError(formPath as Path<CMS1500FormData>, {
                  type: newType,
                  message: annotation.message,
                });
              }
            }
          }
        });
      } else {
        // Clear all scrubber errors if not scrubbed
        currentScrubberFields.forEach(field => {
          formMethods.clearErrors(field as Path<CMS1500FormData>);
        });
      }
    }
  }, [annotationSignature, claim, formMethods]);
  
  // Return a function to clear scrubber error for a specific field
  const clearFieldScrubberError = useCallback((fieldName: string) => {
    if (!formMethods) return;
    
    // Handle nested field paths by traversing the error object
    const parts = fieldName.split('.');
    
    // Navigate to the nested error
    const nestedError = parts.reduce<unknown>((acc, part) => {
      if (acc && typeof acc === 'object' && part in acc) {
        return (acc as Record<string, unknown>)[part];
      }
      return undefined;
    }, formMethods.formState.errors);
    
    // Check if this is a scrubber error and clear it
    if (nestedError && typeof nestedError === 'object' && 'type' in nestedError) {
      const errorObj = nestedError as { type: unknown };
      if (typeof errorObj.type === 'string' && errorObj.type.startsWith('scrubber-')) {
        formMethods.clearErrors(fieldName as Path<CMS1500FormData>);
      }
    }
  }, [formMethods]);
  
  return { clearFieldScrubberError };
};

export const ClaimDetailView: React.FC = () => {
  const { claimId } = useParams<{ claimId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [autosaveStatus, setAutosaveStatus] = useState<AutosaveState>('idle');
  const [isDirty, setIsDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [onRetry, setOnRetry] = useState<(() => void) | undefined>();
  const [formMethods, setFormMethods] = useState<UseFormReturn<CMS1500FormData> | null>(null);

  // Scrubber UI state + scrub mutation
  const { panel, togglePanelCollapse, setPanelWidth, scrubClaim } = useClaimScrubberWithUI();

  // Left sidebar (patient info) local state
  const [patientPanelCollapsed, setPatientPanelCollapsed] = useState(false);
  const [patientPanelWidth, setPatientPanelWidth] = useState(400);

  // Track whether a rebuild was initiated to auto-scrub after processing completes
  const pendingAutoScrubRef = useRef(false);
  const prevStatusRef = useRef<string | undefined>();

  // Get claim data from RTK Query
  const { data: claim } = useGetClaimByIdQuery(
    { claimId: claimId ?? '' },
    { skip: !claimId }
  );

  // Auto-scrub after rebuild finishes processing
  useEffect(() => {
    let isMounted = true;
    const prevStatus = prevStatusRef.current;
    const currentStatus = claim?.status;
    prevStatusRef.current = currentStatus;

    if (
      pendingAutoScrubRef.current &&
      prevStatus === 'processing' &&
      currentStatus &&
      currentStatus !== 'processing' &&
      claimId &&
      isMounted
    ) {
      pendingAutoScrubRef.current = false;
      scrubClaim(claimId);
    }

    return () => {
      isMounted = false;
    };
  }, [claim?.status, claimId, scrubClaim]);

  // Get annotation counts and should show status
  const annotationCounts = selectAnnotationCounts(claim);
  const shouldShowAnnotations = selectShouldShowAnnotations(claim);
  
  // Sync claim annotations with form errors using custom hook
  const { clearFieldScrubberError } = useClaimScrubErrorSync(claim, formMethods);
  
  // Helper function to check if an error is a blocking error
  const isBlockingError = useCallback((error: unknown): boolean => {
    if (!error || typeof error !== 'object') return false;
    
    // Check if this error has a type field
    if ('type' in error) {
      const errorObj = error as { type: unknown };
      if (typeof errorObj.type === 'string') {
        // Block on any error type EXCEPT warnings and info
        return errorObj.type !== 'scrubber-warning' && errorObj.type !== 'scrubber-info';
      }
    }
    
    // If there's a message but no type (regular validation error), it's blocking
    if ('message' in error) {
      const errorObj = error as { message: unknown };
      if (errorObj.message) {
        return true;
      }
    }
    
    // For nested errors, check recursively
    return Object.values(error as Record<string, unknown>).some(isBlockingError);
  }, []);

  // Calculate if form has blocking errors (any error except warnings/info)
  // Access formState.errors directly for better reactivity
  const errors = formMethods?.formState.errors || {};
  const errorEntries = Object.entries(errors);
  
  const hasBlockingErrors = useMemo(() => {
    if (errorEntries.length === 0) {
      return false;
    }
    
    const hasErrors = errorEntries.some(([, error]) => {
      return isBlockingError(error);
    });
    
    return hasErrors;
  }, [errorEntries, isBlockingError]); // Depend on error entries

  // Sticky header detection
  const { registerSentinel, registerHeader, isStuck } = useStickyHeaderDetection();
  const sentinelRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const HEADER_ID = 'claim-edit-header';

  // Register sentinel and header elements - only once on mount
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const header = headerRef.current;

    if (sentinel) {
      registerSentinel(HEADER_ID, sentinel);
    }
    if (header) {
      registerHeader(HEADER_ID, header);
    }

    return () => {
      registerSentinel(HEADER_ID, null);
      registerHeader(HEADER_ID, null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once

  const handleAutosaveStateChange = useCallback(
    (status: AutosaveState, dirty: boolean, savedAt: Date | null, retry: () => void) => {
      setAutosaveStatus(status);
      setIsDirty(dirty);
      setLastSavedAt(savedAt);
      setOnRetry(() => retry); // Wrap in arrow function to store the callback
    },
    [],
  );

  const headerIsStuck = isStuck(HEADER_ID);
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  // Extract encounter ID from the first claim line (claims don't have a top-level encounter_id)
  const encounterId = claim?.claim_lines?.[0]?.encounter_id ?? undefined;

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* Left Column - Patient Info & Billing Codes Panel (Desktop only) */}
      {isDesktop && (claim?.patient_id || encounterId) && (
        <ResizableSidebar
          isOpen
          position="left"
          title="Claim Details"
          width={patientPanelWidth}
          isCollapsed={patientPanelCollapsed}
          onWidthChange={setPatientPanelWidth}
          onCollapseChange={setPatientPanelCollapsed}
        >
          <ClaimLeftSidebarContent
            patientId={claim?.patient_id}
            encounterId={encounterId}
            claimId={claimId ?? ''}
            claimStatus={claim?.status}
            onRebuildStart={() => { pendingAutoScrubRef.current = true; }}
          />
        </ResizableSidebar>
      )}

      {/* Center Column - Main Content */}
      <Box
        sx={{
          flex: '1 1 auto',
          minWidth: 0,
          position: 'relative',
        }}
      >
        {/* Sentinel element for sticky detection */}
        <div
          ref={sentinelRef}
          data-header-id={HEADER_ID}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 1,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />

        {/* Sticky Header */}
        <Box
          ref={headerRef}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: theme.zIndex.appBar - 1,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Container maxWidth="xl">
            <Box
              sx={{
                py: 3,
                borderBottom: headerIsStuck ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
                boxShadow: headerIsStuck ? '0 2px 4px 0 rgba(0, 0, 0, 0.08)' : 'none',
                transition: headerIsStuck
                  ? 'box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), clip-path 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  : 'none', // No transition when unsticking
                clipPath: headerIsStuck
                  ? 'inset(-10px 0 -10px 0)' // Only allow shadow on top and bottom
                  : 'inset(0 0 0 0)', // No clipping when not stuck
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 2,
                }}
              >
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ArrowBackIosIcon
                      onClick={() => navigate('/billing?tab=claims')}
                      sx={{
                        cursor: 'pointer',
                        color: 'text.primary',
                        fontSize: '1.25rem',
                        '&:hover': {
                          color: 'primary.main',
                        },
                      }}
                    />
                    <Typography variant="h4" sx={{ fontWeight: 500 }}>
                      Edit Claim
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    CMS-1500 Health Insurance Claim Form
                  </Typography>
                </Box>

                {/* Right side - AutosaveStatus + Actions */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{
                    flexShrink: 0,
                  }}
                >
                  <AutosaveStatus
                    status={autosaveStatus}
                    isDirty={isDirty}
                    lastSavedAt={lastSavedAt}
                    onRetry={onRetry}
                    verbose={!isMobile}
                  />
                  {claimId && (
                    <>
                      {claim?.status !== 'processing' && (
                        <>
                          <ClaimSubmitButton
                            claimId={claimId}
                            hasErrors={hasBlockingErrors}
                          />
                          <ClaimScrubButton
                            claimId={claimId}
                            asIconButton
                            iconSize="medium"
                          />
                          {(claim?.status === 'draft' || claim?.status === 'ready') && (
                            <ClaimRebuildButton
                              claimId={claimId}
                              claimStatus={claim?.status}
                              iconSize="medium"
                              onRebuildStart={() => { pendingAutoScrubRef.current = true; }}
                            />
                          )}
                        </>
                      )}
                      <DeleteClaimButton claimId={claimId} />
                    </>
                  )}
                </Stack>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Form Content */}
        <Container maxWidth="xl" sx={{ pb: 3 }}>
          {claimId && <ClaimLineageBanner claimId={claimId} />}
          <ClaimEdit
            onAutosaveStateChange={handleAutosaveStateChange}
            onFormMethodsReady={setFormMethods}
            onFieldChange={clearFieldScrubberError}
          />
        </Container>
      </Box>

      {/* Right Column - Resizable Annotations Panel (Desktop only) */}
      {isDesktop && shouldShowAnnotations && (
        <ResizableSidebar
          isOpen={panel.isOpen}
          width={panel.width}
          isCollapsed={panel.isCollapsed}
          onWidthChange={setPanelWidth}
          onCollapseChange={togglePanelCollapse}
          errorCount={annotationCounts.errors}
          warningCount={annotationCounts.warnings}
          infoCount={annotationCounts.info}
        >
          <AnnotationsPanelErrorBoundary claimId={claimId}>
            <ClaimAnnotationsPanel claim={claim} position="sidebar" />
          </AnnotationsPanelErrorBoundary>
        </ResizableSidebar>
      )}
    </Box>
  );
};
